/**
 * The people search behind the header and the single-person read behind the person page (AIA-118). Both use the user directory,
 * `GET /api/users` and `GET /api/users/{id}` (`PersonDirectoryService`), which `entitlement.administer` and `consumption.read.others`
 * both open. A caller without `entitlement.administer` is limited to a search of two to 100 characters; the server answers 400 for
 * anything else and 403 with neither permission.
 */
import { apiFetch } from "./apiFetch";
import type { PersonListPage, PersonSummary } from "./entitlementAdmin";

/** The matches of one search, and whether more people match than were returned. */
export interface PeopleSearchResponse {
  people: PersonSummary[];
  /** True when more people match than were returned. */
  hasMore: boolean;
}

/** The outcome of one search: the response, or a failure of any kind (refused, rejected, server error, network). */
export type PeopleSearchOutcome = { kind: "ok"; response: PeopleSearchResponse } | { kind: "error"; status: number | null };

/**
 * Calls `GET /api/users?search=`.
 *
 * @param q - The text to search for.
 * @param signal - Aborts the request when a newer search replaces it.
 * @returns The matches, or an error outcome carrying the HTTP status (null when the request never got an answer).
 */
export async function searchPeople(q: string, signal?: AbortSignal): Promise<PeopleSearchOutcome> {
  try {
    const response = await apiFetch(`/api/users?search=${encodeURIComponent(q)}`, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal,
    });
    if (response.status !== 200) {
      return { kind: "error", status: response.status };
    }
    const page = (await response.json()) as PersonListPage;
    return { kind: "ok", response: { people: page.items, hasMore: page.totalCount > page.items.length } };
  } catch {
    return { kind: "error", status: null };
  }
}

/** The outcome of reading one person: the person, not found (the same answer for a missing, out-of-scope or hidden person), aborted, or a failure. */
export type PersonOutcome = { kind: "ok"; person: PersonSummary } | { kind: "notFound" } | { kind: "aborted" } | { kind: "error"; status: number | null };

/**
 * Calls `GET /api/users/{id}`, which applies the directory's visibility rules and answers one fixed 404 for a person who does not exist,
 * is outside the caller's scope, or is Inactive and not visible to the caller.
 *
 * @param id - The person's identifier.
 * @param signal - Aborts the request when the page no longer wants the answer.
 * @returns The person, `notFound`, `aborted`, or an error outcome carrying the HTTP status (null when the request never got an answer).
 */
export async function getPerson(id: string, signal?: AbortSignal): Promise<PersonOutcome> {
  try {
    const response = await apiFetch(`/api/users/${encodeURIComponent(id)}`, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal,
    });
    if (response.status === 404) {
      return { kind: "notFound" };
    }
    if (response.status !== 200) {
      return { kind: "error", status: response.status };
    }
    return { kind: "ok", person: (await response.json()) as PersonSummary };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return { kind: "aborted" };
    }
    return { kind: "error", status: null };
  }
}
